// ─────────────────────────────────────────────
//  TutorialStories.jsx — Tutorial estilo historias
// ─────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './TutorialStories.module.css';
import { TUTORIAL_SLIDES, SLIDE_DURATION_MS } from './tutorialSlides';

const SWIPE_THRESHOLD = 52;
const TAP_MOVE_LIMIT = 14;
const TAP_TIME_LIMIT = 320;

export default function TutorialStories({ open, onClose }) {
	const [index, setIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const [dragPx, setDragPx] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const timerRef = useRef(null);
	const viewportRef = useRef(null);
	const gestureRef = useRef({
		pointerId: null,
		startX: 0,
		startY: 0,
		startTime: 0,
		moved: false,
	});

	const slide = TUTORIAL_SLIDES[index];

	const goNext = useCallback(() => {
		setIndex((prev) => {
			if (prev >= TUTORIAL_SLIDES.length - 1) {
				onClose();
				return 0;
			}
			return prev + 1;
		});
	}, [onClose]);

	const goPrev = useCallback(() => {
		setIndex((prev) => Math.max(0, prev - 1));
	}, []);

	const handleClose = useCallback(() => {
		setIndex(0);
		setDragPx(0);
		onClose();
	}, [onClose]);

	useEffect(() => {
		if (!open) return undefined;

		const onKeyDown = (e) => {
			if (e.key === 'Escape') handleClose();
			if (e.key === 'ArrowRight') goNext();
			if (e.key === 'ArrowLeft') goPrev();
		};

		document.body.style.overflow = 'hidden';
		window.addEventListener('keydown', onKeyDown);

		return () => {
			document.body.style.overflow = '';
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [open, handleClose, goNext, goPrev]);

	useEffect(() => {
		if (!open || paused || isDragging) return undefined;

		timerRef.current = window.setTimeout(goNext, SLIDE_DURATION_MS);
		return () => window.clearTimeout(timerRef.current);
	}, [open, index, paused, isDragging, goNext]);

	useEffect(() => {
		if (open) {
			setIndex(0);
			setDragPx(0);
			setIsDragging(false);
		}
	}, [open]);

	const finishGesture = useCallback((clientX, rect) => {
		const g = gestureRef.current;
		const deltaX = clientX - g.startX;

		if (g.moved && Math.abs(deltaX) >= SWIPE_THRESHOLD) {
			if (deltaX < 0) goNext();
			else goPrev();
		} else if (!g.moved) {
			const elapsed = Date.now() - g.startTime;
			if (elapsed <= TAP_TIME_LIMIT && rect) {
				const x = clientX - rect.left;
				if (x < rect.width * 0.35) goPrev();
				else goNext();
			}
		}

		setDragPx(0);
		setIsDragging(false);
		g.moved = false;
		g.pointerId = null;
	}, [goNext, goPrev]);

	const onPointerDown = (e) => {
		if (e.pointerType === 'mouse' && e.button !== 0) return;

		gestureRef.current = {
			pointerId: e.pointerId,
			startX: e.clientX,
			startY: e.clientY,
			startTime: Date.now(),
			moved: false,
		};

		setPaused(true);
		e.currentTarget.setPointerCapture(e.pointerId);
	};

	const onPointerMove = (e) => {
		const g = gestureRef.current;
		if (g.pointerId !== e.pointerId) return;

		const deltaX = e.clientX - g.startX;
		const deltaY = e.clientY - g.startY;

		if (!g.moved && (Math.abs(deltaX) > TAP_MOVE_LIMIT || Math.abs(deltaY) > TAP_MOVE_LIMIT)) {
			if (Math.abs(deltaX) >= Math.abs(deltaY)) {
				g.moved = true;
				setIsDragging(true);
			}
		}

		if (g.moved) {
			const atStart = index === 0 && deltaX > 0;
			const atEnd = index === TUTORIAL_SLIDES.length - 1 && deltaX < 0;
			const resistance = atStart || atEnd ? 0.35 : 1;
			setDragPx(deltaX * resistance);
		}
	};

	const onPointerUp = (e) => {
		const g = gestureRef.current;
		if (g.pointerId !== e.pointerId) return;

		const rect = viewportRef.current?.getBoundingClientRect() ?? null;
		finishGesture(e.clientX, rect);
		setPaused(false);

		if (e.currentTarget.hasPointerCapture(e.pointerId)) {
			e.currentTarget.releasePointerCapture(e.pointerId);
		}
	};

	const onPointerCancel = (e) => {
		gestureRef.current.pointerId = null;
		gestureRef.current.moved = false;
		setDragPx(0);
		setIsDragging(false);
		setPaused(false);
	};

	if (!open) return null;

	const trackStyle = {
		transform: `translate3d(calc(${-index * 100}% + ${dragPx}px), 0, 0)`,
		transition: isDragging ? 'none' : 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
	};

	return createPortal(
		<div
			className={styles.overlay}
			role="dialog"
			aria-modal="true"
			aria-label="Tutorial de AR Cuánto"
		>
			<div
				className={styles.viewer}
				style={{ '--slide-accent': slide.accent }}
			>
				<div className={styles.progressRow}>
					{TUTORIAL_SLIDES.map((item, i) => (
						<div key={item.id} className={styles.progressTrack}>
							<div
								className={`${styles.progressFill} ${
									i < index ? styles.progressDone : ''
								} ${i === index ? styles.progressActive : ''} ${
									(paused || isDragging) && i === index ? styles.progressPaused : ''
								}`}
								style={
									i === index
										? { animationDuration: `${SLIDE_DURATION_MS}ms` }
										: undefined
								}
							/>
						</div>
					))}
				</div>

				<header className={styles.header}>
					<div className={styles.headerMeta}>
						<span className={styles.headerIcon} aria-hidden="true">{slide.icon}</span>
						<span className={styles.headerTitle}>{slide.title}</span>
					</div>
					<button
						type="button"
						className={styles.closeBtn}
						onClick={handleClose}
						aria-label="Cerrar tutorial"
					>
						✕
					</button>
				</header>

				<div
					ref={viewportRef}
					className={styles.viewport}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerCancel}
				>
					<div className={styles.tapHints} aria-hidden="true">
						<span className={styles.tapZoneLeft} />
						<span className={styles.tapZoneRight} />
					</div>

					<div className={styles.track} style={trackStyle}>
						{TUTORIAL_SLIDES.map((item) => (
							<article
								key={item.id}
								className={styles.slidePanel}
								style={{ '--slide-accent': item.accent }}
								aria-hidden={item.id !== slide.id}
							>
								{/* <div className={styles.imageWrap}>
									<img
										className={styles.image}
										src={item.image}
										alt=""
										draggable="false"
									/>
								</div> */}

								<div className={styles.textBlock}>
									<p className={styles.subtitle}>{item.subtitle}</p>
									<p className={styles.body}>{item.body}</p>

									{item.isFinal && (
										<p className={styles.suggestion}>
											¿Tienes ideas? <br />Escríbenos con tus sugerencias para mejorar la app:<br />
											<a
												className={styles.mailLink}
												href="mailto:hola@armedia.dev"
												onPointerDown={(e) => e.stopPropagation()}
												onClick={(e) => e.stopPropagation()}
											>
												hola@armedia.dev
											</a>
										</p>
									)}
								</div>
							</article>
						))}
					</div>
				</div>

				{/* <footer className={styles.footer}>
					<span className={styles.counter}>
						{index + 1} / {TUTORIAL_SLIDES.length}
					</span>
					<span className={styles.hint}>Desliza o toca los lados</span>
					{isLast ? (
						<button
							type="button"
							className={styles.footerBtn}
							onClick={(e) => { e.stopPropagation(); handleClose(); }}
						>
							Cerrar
						</button>
					) : (
						<button
							type="button"
							className={styles.footerBtn}
							onClick={(e) => { e.stopPropagation(); goNext(); }}
						>
							Siguiente
						</button>
					)}
				</footer> */}
			</div>
		</div>,
		document.body
	);
}
